import { useEffect, useRef, useState } from 'react'
import type { DripAudience, DripPreview, Option, Vocabulary } from '../api/types'
import { titleCase } from '../lib/format'
import { Icon } from './Icon'
import { Loading } from './States'

// The list of people, filtered from its own column headings and tickable row by
// row. Used to choose a drip's audience and to pick who a new workflow starts
// for, so both screens answer the same question the same way.

export type FilterKey = 'lead_status' | 'campaign' | 'source' | 'purpose' | 'timeline'
export interface FilterList { key: FilterKey; label: string; options: Option[] }

/** Reads a stored value back as the label officers picked it by. */
export function optionLabel(options: Option[] | undefined, value: string | null): string {
  if (!value) return '—'
  return options?.find((o) => o.key === value)?.label ?? titleCase(value)
}

export function PeopleTable({ lists, audience, onFilter, preview, loading, vocab, selected, onToggle, onToggleShown, emptyNote }: {
  lists: FilterList[]
  audience: DripAudience
  /** Set a filter's values; an empty list clears it. */
  onFilter: (key: FilterKey, values: string[]) => void
  preview: DripPreview | null
  loading?: boolean
  vocab: Vocabulary | null
  /** Ticked rows. Omit onToggle to show the table without checkboxes. */
  selected?: Set<number>
  onToggle?: (id: number, name: string | null) => void
  onToggleShown?: (ids: { id: number; name: string | null }[], checked: boolean) => void
  emptyNote?: string
}) {
  const people = preview?.people ?? []
  const pickable = Boolean(onToggle)
  const allShown = pickable && people.length > 0 && people.every((p) => selected?.has(p.id))

  return (
    <div className="aud-table">
      <div className={`aud-tr aud-th${pickable ? ' pickable' : ''}`}>
        {pickable ? (
          <span>
            <button className="aud-check" aria-label={allShown ? 'Unpick everyone shown' : 'Pick everyone shown'}
              title={allShown ? 'Unpick everyone shown' : 'Pick everyone shown'}
              onClick={() => onToggleShown?.(people.map((p) => ({ id: p.id, name: p.name })), !allShown)}>
              <Icon name={allShown ? 'check_box' : 'check_box_outline_blank'} size={17} />
            </button>
          </span>
        ) : null}
        <span>
          Name
          <span className="quiet" style={{ fontWeight: 400, marginLeft: 6 }}>
            {!preview ? '…'
              : preview.count ? `${preview.count.toLocaleString()} ${preview.count === 1 ? 'person' : 'people'}`
                : `0 of ${preview.shown_count.toLocaleString()} picked`}
          </span>
        </span>
        {lists.map((l) => (
          <FilterHead key={l.key} label={l.label} options={l.options}
            selected={audience[l.key] ?? []}
            onToggle={(value) => {
              const current = audience[l.key] ?? []
              onFilter(l.key, current.includes(value) ? current.filter((v) => v !== value) : [...current, value])
            }}
            onClear={() => onFilter(l.key, [])} />
        ))}
      </div>
      <div className="aud-rows">
        {loading && !preview ? <div className="aud-empty"><Loading /></div>
          : !people.length ? <div className="aud-empty quiet">{emptyNote ?? 'Nobody here.'}</div>
            : people.map((c) => {
              const on = selected?.has(c.id) ?? false
              return (
                <div key={c.id} className={`aud-tr${pickable ? ' pickable' : ''}${on ? ' picked' : ''}`}>
                  {pickable ? (
                    <span>
                      <button className="aud-check" aria-label={`${on ? 'Unpick' : 'Pick'} ${c.name ?? 'this person'}`}
                        onClick={() => onToggle?.(c.id, c.name)}>
                        <Icon name={on ? 'check_box' : 'check_box_outline_blank'} size={17}
                          style={{ color: on ? 'var(--accent)' : 'var(--ink-4)' }} />
                      </button>
                    </span>
                  ) : null}
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 500 }}>{c.name ?? `#${c.id}`}</span>
                    {c.city ? <span className="quiet" style={{ fontSize: 11.5, display: 'block' }}>{titleCase(c.city)}</span> : null}
                  </span>
                  <span>{optionLabel(vocab?.lead_status, c.status)}</span>
                  <span className="quiet">{c.campaign ?? '—'}</span>
                  <span className="quiet">{optionLabel(vocab?.timeline, c.timeline)}</span>
                  <span className="quiet">{optionLabel(vocab?.purpose, c.purpose)}</span>
                  <span className="quiet">{optionLabel(vocab?.source, c.source)}</span>
                </div>
              )
            })}
      </div>
    </div>
  )
}


/** A column heading that is also its filter: click it, tick the values you want.
 *  The list of names under it narrows as you tick, so nobody has to imagine who
 *  a filter means. */
function FilterHead({ label, options, selected, onToggle, onClear }: {
  label: string
  options: Option[]
  selected: string[]
  onToggle: (value: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc) }
  }, [open])

  return (
    <span className="aud-head" ref={ref}>
      <button className={`aud-head-btn${selected.length ? ' on' : ''}`} onClick={() => setOpen((o) => !o)}
        aria-expanded={open} disabled={!options.length}
        title={selected.length ? `Filtered: ${selected.length} chosen` : `Filter by ${label.toLowerCase()}`}>
        {label}
        {selected.length ? <span className="aud-head-n">{selected.length}</span> : null}
        <Icon name="arrow_drop_down" size={16} />
      </button>
      {open ? (
        <span className="aud-menu">
          {options.map((o) => (
            <button key={o.key} className="aud-menu-item" onClick={() => onToggle(o.key)}>
              <Icon name={selected.includes(o.key) ? 'check_box' : 'check_box_outline_blank'} size={16}
                style={{ color: selected.includes(o.key) ? 'var(--accent)' : 'var(--ink-4)' }} />
              {o.label}
            </button>
          ))}
          {selected.length ? (
            <button className="aud-menu-item" style={{ color: 'var(--ink-2)' }} onClick={() => { onClear(); setOpen(false) }}>
              <Icon name="close" size={16} /> Clear this filter
            </button>
          ) : null}
        </span>
      ) : null}
    </span>
  )
}
